import { type ReactNode, useState } from 'react';
import type { CategorieNdf, Depense } from '../../types/ndf';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Case,
  Champ,
  Saisie,
  SaisieNombre,
  Selection,
} from './ui/Champ';
import type { ErreursChamps } from './ui/erreurs-champs';
import { RattachementClient } from './RattachementClient';
import { ConversionDevise } from './ConversionDevise';

/**
 * LE formulaire de dépense — création ET modification.
 *
 * Un seul formulaire, jamais deux. Quand la création et la modification vivaient
 * dans deux fichiers, le rattachement client n'existait que dans l'un des deux :
 * cocher « Refacturable » en modification ne produisait rien, et l'erreur
 * renvoyée visait un champ absent de l'écran, donc invisible. Toute évolution
 * doit se faire ICI pour valoir des deux côtés.
 *
 * Le formulaire PROPOSE, le serveur DÉCIDE : la TVA est reforcée à zéro selon la
 * catégorie et le TTC recalculé côté serveur. Le TTC affiché n'est qu'une
 * prévisualisation honnête.
 */

export interface ChampsDepense {
  /** Devise du justificatif. null ou 'EUR' = dépense en euros, cas courant. */
  devise: string | null;
  /** Montant payé DANS cette devise, TTC. */
  montant_original: number | null;
  /** Taux appliqué pour arriver aux euros, conservé pour rester justifiable. */
  taux_change: number | null;
  date_depense: string;
  categorie_id: string;
  sous_categorie_id: string;
  description: string;
  quantite: number;
  prix_unitaire_ht: number;
  montant_tva: number;
  demande_remboursement: boolean;
  facturable: boolean;
  client_crm_id: string | null;
  client_nom: string | null;
  mission_crm_id: string | null;
  mission_nom: string | null;
  type_mission: string | null;
}

/** Valeurs de départ d'une nouvelle dépense — tout ce qui peut l'être est pré-rempli. */
export function champsVides(): ChampsDepense {
  return {
    devise: null,
    montant_original: null,
    taux_change: null,
    // Date locale, pas `toISOString()` : à Paris, celui-ci recule d'un jour en soirée.
    date_depense: new Date().toLocaleDateString('sv-SE'),
    categorie_id: '',
    sous_categorie_id: '',
    description: '',
    quantite: 1,
    prix_unitaire_ht: 0,
    montant_tva: 0,
    demande_remboursement: true,
    facturable: false,
    client_crm_id: null,
    client_nom: null,
    mission_crm_id: null,
    mission_nom: null,
    type_mission: null,
  };
}

/** Champs d'une dépense existante, remis dans la forme attendue par le formulaire. */
export function champsDepuis(d: Depense): ChampsDepense {
  return {
    devise: d.devise ?? null,
    montant_original: d.montant_original ?? null,
    taux_change: d.taux_change ?? null,
    date_depense: d.date_depense,
    categorie_id: d.categorie_id,
    sous_categorie_id: d.sous_categorie_id ?? '',
    description: d.description ?? '',
    quantite: d.quantite,
    prix_unitaire_ht: d.prix_unitaire_ht,
    montant_tva: d.montant_tva,
    demande_remboursement: d.demande_remboursement,
    facturable: d.facturable,
    client_crm_id: d.client_crm_id ?? null,
    client_nom: d.client_nom ?? null,
    mission_crm_id: d.mission_crm_id ?? null,
    mission_nom: d.mission_nom ?? null,
    type_mission: d.type_mission ?? null,
  };
}

/** Les champs que ce formulaire affiche — sert à repérer une erreur sans champ visible. */
export const CHAMPS_AFFICHES = [
  'date_depense',
  'categorie_id',
  'sous_categorie_id',
  'quantite',
  'prix_unitaire_ht',
  'montant_tva',
  'description',
  'devise',
  'montant_original',
  'taux_change',
  'demande_remboursement',
  'facturable',
  'client_crm_id',
  'mission_crm_id',
] as const;

export function FormulaireDepense({
  valeurInitiale,
  categories,
  erreurs,
  enCours,
  libelleValider,
  avantChamps,
  apresActions,
  onAnnuler,
  onSoumettre,
}: {
  valeurInitiale: ChampsDepense;
  categories: CategorieNdf[];
  erreurs: ErreursChamps;
  enCours: boolean;
  libelleValider: string;
  /** Bloc propre à l'écran appelant (la photo du ticket, à la création). */
  avantChamps?: ReactNode;
  apresActions?: ReactNode;
  onAnnuler: () => void;
  onSoumettre: (champs: ChampsDepense) => void;
}) {
  const [f, setF] = useState<ChampsDepense>(valeurInitiale);
  // Le bloc devise s'ouvre de lui-même quand la lecture du justificatif a
  // trouvé une devise étrangère : le salarié n'a pas à savoir qu'il existe.
  const [deviseOuverte, setDeviseOuverte] = useState(
    Boolean(valeurInitiale.devise && valeurInitiale.devise !== 'EUR'),
  );

  const racines = categories.filter((c) => !c.parent_id);
  const categorie = categories.find((c) => c.id === f.categorie_id);
  const sousCategories = categories.filter((c) => c.parent_id === f.categorie_id);
  const tvaVerrouillee = categorie?.regle_tva === 'forcee_zero';
  const tva = tvaVerrouillee ? 0 : f.montant_tva;
  const ttc = Math.round((f.quantite * f.prix_unitaire_ht + tva) * 100) / 100;

  return (
    <form
      className="space-y-4 pb-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSoumettre({ ...f, montant_tva: tva });
      }}
    >
      {avantChamps}

      <section className="space-y-4 rounded-lg border border-nuit/10 bg-white p-4">
        <Champ label="Date" requis erreur={erreurs.date_depense}>
          <Saisie
            type="date"
            required
            value={f.date_depense}
            onChange={(e) => setF({ ...f, date_depense: e.target.value })}
          />
        </Champ>

        <Champ label="Catégorie" requis erreur={erreurs.categorie_id}>
          <Selection
            required
            value={f.categorie_id}
            onChange={(e) => setF({ ...f, categorie_id: e.target.value, sous_categorie_id: '' })}
          >
            <option value="">Choisir…</option>
            {racines.map((c) => (
              <option key={c.id} value={c.id}>
                {c.libelle}
              </option>
            ))}
          </Selection>
        </Champ>

        {/* Champ conditionnel : il apparaît, il n'est jamais grisé en permanence. */}
        {sousCategories.length > 0 && (
          <Champ
            label="Sous-catégorie"
            requis={categorie?.sous_categorie_obligatoire}
            erreur={erreurs.sous_categorie_id}
          >
            <Selection
              value={f.sous_categorie_id}
              onChange={(e) => setF({ ...f, sous_categorie_id: e.target.value })}
            >
              <option value="">Choisir…</option>
              {sousCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.libelle}
                </option>
              ))}
            </Selection>
          </Champ>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Champ label="Quantité" erreur={erreurs.quantite}>
            <SaisieNombre valeur={f.quantite} onValeur={(v) => setF({ ...f, quantite: v ?? 0 })} />
          </Champ>
          <Champ label="Prix unitaire HT" requis erreur={erreurs.prix_unitaire_ht}>
            <SaisieNombre
              valeur={f.prix_unitaire_ht}
              onValeur={(v) => setF({ ...f, prix_unitaire_ht: v ?? 0 })}
            />
          </Champ>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Champ
            label="Montant TVA"
            erreur={erreurs.montant_tva}
            aide={tvaVerrouillee ? 'La TVA n’est pas récupérable sur cette catégorie.' : undefined}
          >
            <SaisieNombre
              disabled={tvaVerrouillee}
              valeur={tva}
              onValeur={(v) => setF({ ...f, montant_tva: v ?? 0 })}
            />
          </Champ>
          {/* Champ calculé : fond distinct, jamais saisissable. */}
          <Champ label="Montant TTC">
            <div className="flex min-h-11 items-center rounded-md bg-ciel/25 px-3 text-sm font-semibold text-nuit">
              {ttc.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
            </div>
          </Champ>
        </div>

        <Champ label="Description">
          {/* Une description est une phrase : majuscule initiale, correction
              active, et la touche d'entrée annonce la suite du formulaire. */}
          <Saisie
            value={f.description}
            placeholder="Facultatif"
            autoCapitalize="sentences"
            enterKeyHint="next"
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </Champ>

        {/* Cas rare : il ne doit pas encombrer l'écran des dépenses en euros,
            qui sont l'immense majorité. */}
        {!deviseOuverte ? (
          <button
            type="button"
            onClick={() => setDeviseOuverte(true)}
            className="min-h-11 text-sm text-nuit underline"
          >
            Cette dépense a été payée dans une autre devise
          </button>
        ) : (
          <div className="space-y-3 border-t border-nuit/10 pt-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-titre text-sm font-bold text-nuit">Paiement en devise</h3>
              <button
                type="button"
                onClick={() => {
                  setDeviseOuverte(false);
                  setF({ ...f, devise: null, montant_original: null, taux_change: null });
                }}
                className="min-h-11 text-sm text-nuit underline"
              >
                Retirer
              </button>
            </div>
            <ConversionDevise
              devise={f.devise}
              montantOriginal={f.montant_original}
              taux={f.taux_change}
              onChange={(v) =>
                setF({
                  ...f,
                  devise: v.devise,
                  montant_original: v.montant_original,
                  taux_change: v.taux_change,
                  // La conversion ALIMENTE le prix unitaire, qui reste
                  // modifiable ensuite : c'est le relevé bancaire qui fait foi.
                  prix_unitaire_ht: v.montant_eur ?? f.prix_unitaire_ht,
                  quantite: v.montant_eur !== null ? 1 : f.quantite,
                })
              }
            />
          </div>
        )}
      </section>

      <section className="rounded-lg border border-nuit/10 bg-white p-4">
        <Case
          label="Demander le remboursement"
          aide="Décochez si vous avez réglé avec la carte de l’entreprise."
          checked={f.demande_remboursement}
          onChange={(e) => setF({ ...f, demande_remboursement: e.target.checked })}
        />
        <Case
          label="Refacturable à un client"
          // La règle de refacturation dépend de ce qui est LU sur le
          // justificatif : une TVA recalculée de tête ferait facturer un HT
          // au client là où le TTC lui est dû.
          aide="Ne saisissez une TVA que si elle figure sur le justificatif."
          checked={f.facturable}
          onChange={(e) => setF({ ...f, facturable: e.target.checked })}
        />
        {f.facturable && (
          <div className="mt-3 border-t border-nuit/10 pt-3">
            <RattachementClient
              clientId={f.client_crm_id}
              clientNom={f.client_nom}
              missionId={f.mission_crm_id}
              onChange={(v) => setF({ ...f, ...v })}
            />
          </div>
        )}
      </section>

      <div className="sticky bottom-24 flex gap-2 md:bottom-0">
        <BoutonSecondaire type="button" disabled={enCours} onClick={onAnnuler}>
          Annuler
        </BoutonSecondaire>
        <BoutonPrincipal type="submit" disabled={enCours} className="flex-1">
          {enCours ? 'Enregistrement…' : libelleValider}
        </BoutonPrincipal>
      </div>

      {apresActions}
    </form>
  );
}
